/* eslint-disable @typescript-eslint/naming-convention */
import {
    ICouponsAPIResponse,
    NativeLocalCart,
    ReviewedCartApiResponse,
    EventStatusResponse,
    JobSubmitResponse,
    JobStatusResponse,
    PreSignedUrlResponse,
} from "@swiggy-private/cart-types";
import {
    CouponPageType,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    addAdditionalHeadersToPLAPI,
    setCartTypeInSession,
} from "@swiggy-private/checkout-core";
import { ApiResponse } from "@cart-client/interfaces/Api";

// helpers
import { makeDownstreamApiCall } from "@cart-client/utils/Api";
import { getUpdateCartParams } from "@cart-client/helpers/ApiHelper";
import { getGandalfApiHeaders } from "@cart-client/helpers/gandalfApiHeaders";
import { isEmpty } from "@swiggy-private/js-utils";
import { isEmptyCartWithStealDealCheck } from "@cart-client/helpers/StealDealCart";
import { DOWNSTREAM_HOSTS } from "@cart-client/constants/Hosts";

const ENDPOINT = {
    APPLY_COUPON_PUBLIC: "/api/v3/apply-coupon",
    UPDATE_CART_PUBLIC: "/api/v3/update-cart",
    FLUSH_CART: "/api/v3/flush-cart",
    UPDATE_GUEST_CART: "/api/v3/update-guest-cart",
    FETCH_COUPONS: "/api/v3/fetch-coupons",
    EVENT_STATUS: "/api/v1/events/event-status",
    PRE_SIGNED_URL: "/api/presignedurl",
    JOB_SUBMIT: "/api/job/submit",
    JOB_STATUS: "/api/job/checkstatus",
    LOG_CART_EVENT: "/api/v3/log-update-cart-event",
};

export async function updateCartPublic(
    apiHost: string,
    params: NativeLocalCart,
): Promise<ApiResponse<ReviewedCartApiResponse>> {
    // If cart is empty and is guest cart, return success directly
    const isGuest =
        isEmpty(GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId)) ||
        isEmpty(GlobalHeaderState.getHeader(SwgyHeaderKeysList.token));

    //  Use steal deal logic: cart is "empty" if it contains only steal deal items or is truly empty
    const isEmptyCart = isEmptyCartWithStealDealCheck(params.cartItems, params.mealItems);

    if (isEmptyCart && isGuest) {
        setCartTypeInSession({});
        return Promise.resolve({
            statusCode: 0,
            data: {} as ReviewedCartApiResponse,
        });
    }
    let httpMethod = "POST";
    let endpoint = ENDPOINT.UPDATE_CART_PUBLIC;
    let bodyParams = getUpdateCartParams(params);
    if (isEmptyCart) {
        endpoint = ENDPOINT.FLUSH_CART;
        httpMethod = "DELETE";
        bodyParams = {};
    } else if (isGuest) {
        endpoint = ENDPOINT.UPDATE_GUEST_CART;
    }

    const response = await makeDownstreamApiCall<ApiResponse<ReviewedCartApiResponse>>({
        apiHost,
        endpoint,
        bodyParams: bodyParams,
        options: { method: httpMethod },
        onResponseCb: (res) => {
            const cartResponse = res as ApiResponse<ReviewedCartApiResponse>;
            setCartTypeInSession(cartResponse?.data ?? {});
            // we only want to set cart type in session. Hence retruning empty object
            return {};
        },
    });
    return response;
}

export async function applyCouponPublic(
    apiHost: string,
    couponCode: string,
): Promise<ApiResponse<ReviewedCartApiResponse>> {
    const response = await makeDownstreamApiCall<ApiResponse<ReviewedCartApiResponse>>({
        apiHost,
        endpoint: ENDPOINT.APPLY_COUPON_PUBLIC,
        bodyParams: { coupon_code: couponCode },
        options: {
            method: "POST",
        },
    });
    return response;
}

export async function fetchCouponsPublic({
    apiHost,
    cartValue,
    restaurantId,
    couponCode = "",
    subpage = CouponPageType.RESTAURANT_COUPON_LISTING,
    isNewPORFlow = false,
}: {
    apiHost: string;
    cartValue: string;
    restaurantId: string;
    couponCode?: string;
    subpage?: CouponPageType;
    isNewPORFlow?: boolean;
}): Promise<ICouponsAPIResponse> {
    const params = {
        source_page: "cart",
        restaurant_id: restaurantId,
        cart_value: cartValue,
        coupon_code: couponCode,
        lat: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat),
        lng: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng),
        sub_page: isNewPORFlow ? subpage : "",
    };

    const response = await makeDownstreamApiCall<ICouponsAPIResponse>({
        apiHost,
        endpoint: ENDPOINT.FETCH_COUPONS,
        searchParams: params,
        options: {
            method: "GET",
            headers: addAdditionalHeadersToPLAPI(),
        },
    });

    return response;
}

export async function logUpdateCartEvent(apiHost: string): Promise<void> {
    await makeDownstreamApiCall<void>({
        apiHost,
        endpoint: ENDPOINT.LOG_CART_EVENT,
        bodyParams: {},
        options: { method: "POST" },
    });
}

export async function fetchEventStatusPublic(): Promise<EventStatusResponse> {
    const response = await makeDownstreamApiCall<EventStatusResponse>({
        apiHost: DOWNSTREAM_HOSTS.PARTNER_REWARDS_HOST,
        endpoint: ENDPOINT.EVENT_STATUS,
        bodyParams: {
            event_names: ["FLOATY_TYPE_IPL"],
        },
        options: {
            method: "POST",
        },
    });
    return response;
}

export async function fetchPreSignedUrl(): Promise<PreSignedUrlResponse> {
    const response = await makeDownstreamApiCall<PreSignedUrlResponse>({
        apiHost: DOWNSTREAM_HOSTS.GANDALF_HOST,
        endpoint: ENDPOINT.PRE_SIGNED_URL,
        excludeDefaultHeaders: true,
        options: {
            method: "GET",
            headers: getGandalfApiHeaders(),
        },
    });
    return response;
}

export async function postJobSubmit(preSignedUrl: string, requestId: string): Promise<JobSubmitResponse> {
    const response = await makeDownstreamApiCall<JobSubmitResponse>({
        apiHost: DOWNSTREAM_HOSTS.GANDALF_HOST,
        endpoint: ENDPOINT.JOB_SUBMIT,
        bodyParams: {
            presigned_url: preSignedUrl,
            request_id: requestId,
        },
        excludeDefaultHeaders: true,
        options: {
            method: "POST",
            headers: getGandalfApiHeaders(),
        },
    });
    return response;
}

export async function fetchJobStatus(requestId: string): Promise<JobStatusResponse> {
    const response = await makeDownstreamApiCall<JobStatusResponse>({
        apiHost: DOWNSTREAM_HOSTS.GANDALF_HOST,
        endpoint: ENDPOINT.JOB_STATUS,
        bodyParams: {
            request_id: requestId,
        },
        excludeDefaultHeaders: true,
        options: {
            method: "POST",
            headers: getGandalfApiHeaders(),
        },
    });
    return response;
}
